import { Injectable } from '@angular/core';
import {
	HttpInterceptor,
	HttpHandler,
	HttpRequest,
	HttpEvent,
	HttpResponse,
	HttpErrorResponse,
} from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { DeploymentContext } from '../utilities/deployment-context/deployment-context.js';
import { ProgressIndicatorService } from '../services/progress-indicator/progress-indicator.service.js';
import { AuthnStrings } from '../constants/AuthnStrings.js';
import { TokenService } from '../services/token/token.service.js';
import { ApplicationContext } from '../utilities/application-context/application-context.js';
import { AuthnService } from '../services/authn/authn.service.js';
import { Constants } from '../../../../company-finder-common/src/constants.js';

@Injectable({ providedIn: 'root' })
export class AuthnInterceptor implements HttpInterceptor {
	private progress = {};
	private _timer = {};
	private _defaultTimeInterval = 1800;

	constructor(
		private appContext: ApplicationContext,
		private tokenService: TokenService,
		private deploymentContext: DeploymentContext,
		private progressIndicatorService: ProgressIndicatorService,
		private authnService: AuthnService
	) {}

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		this.startIndicator(request.url);
		this.progress[request.url] = true;

		if (this.tokenService.isAuthenticated) {
			// FUTURE: Given that our app may initiate xhr requests to systems other than Navigator (currently just JUniverse),
			//   we might consider only adding this auth header to requests sent to the Navigator API.
			request = request.clone({
				setHeaders: {
					Authorization: `Bearer ${this.tokenService.getToken()}`,
				},
			});
		}

		// Any time a token is received, hold onto it
		return next.handle(request).pipe(
			map((event: HttpEvent<unknown>) => {
				if (event instanceof HttpResponse) {
					if (event.status === 200 || event.status === 302) {
						// This logic is to avoid cache hits on "anonymous endpoints" unexpectedly forcing a logout.
						// For example, an aggregate Asset search occurs in MainComponent.initializeData
						// that resulted in a cached anonymous response, and after a user authenticated, the same request resulted in a cache-hit
						// that does not have a bearer token, and hence the logic below forced a clearToken() logout.
						if (!event.url.includes(Constants.PARAM_CACHEABLE)) {
							const returnedToken = event.headers.get(
								AuthnStrings.RESPONSE_HEADER_TOKEN_KEY
							);
							// if user has intentionally logged out, ignore token
							if (
								returnedToken &&
								!this.appContext.jnjLoggedOut
							) {
								this.authnService.setTokenAndScheduleRefresh(
									returnedToken
								);
							} else {
								// If the user had a token, it's not valid. Clear it out
								this.authnService.clearToken();
								this.authnService.clearJnjWasLoggedIn();
							}
						}
					}
				}
				return event;
			}),
			catchError((error: HttpErrorResponse) => {
				if (error.status === 401) {
					// we don't want to log out a user or redirect them if they don't have a JUNIVERSE login.
					// As part of ADJQ-750 we investigated removing this 401 from the log, but Chrome is logging
					// the error before it is handed to Angular
					if (
						this.deploymentContext.juniverseEnabled &&
						error.url.startsWith(
							this.deploymentContext.juniverseTokenEndpoint
						)
					) {
						this.stopIndicator(request.url);
						return of(null);
					}

					if (error.url.indexOf('/api/v1/') > -1) {
						this.tokenService.clearToken();
					}

					// To interrupt the current route, need to throw an error, but one the catch-all JnjErrorHandler will ignore.
					// This was "proven" based on logging pre & post httpclient invocation; "return EMPTY" did not interrupt execution.
					// See https://github.com/angular/angular/issues/21149 for some context, although that didn't provide decisiveclarity
					return of(null);
				} else {
					return throwError(error);
				}
			}),
			finalize(() => {
				this.stopIndicator(request.url);
			})
		);
	}

	private startIndicator(url: string) {
		const timerInterval =
			this.deploymentContext.progressIndicator?.delay ||
			this._defaultTimeInterval;

		// Clear any old timer for this url before we start a new one
		// Better might be to pass in a unique id for this specific httprequest invocation, not just by url, but this
		// is a quick fix to the problem where the progress indicator pops up if you do two requests to the same url in a row
		// with a particular timing.
		clearInterval(this._timer[url]);

		this._timer[url] = setInterval(() => {
			if (this.progress[url]) {
				this.progressIndicatorService.startOperation();
			}
		}, timerInterval);
	}

	private stopIndicator(url: string) {
		clearInterval(this._timer[url]);
		this.progress[url] = false;
		this.progressIndicatorService.completeOperation();
	}
}
